import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { DonateButtonVariant } from '../Button/Button.jsx';
import { Images } from '../../../utils/Images';
import { getImageSrc } from '../../../utils/imageMapping';
import { getCampaignShareDetails } from '../../../api/services/campaignService';
import { formatCompactInr } from '../../../utils/formatCompactInr';
import { getAvailableSocialPlatforms, buildSocialShareUrl } from '../../../utils/socialShare';

// ─── Styled components ────────────────────────────────────────────────────────

const StyledCard = styled(Card)(({ isMobile }) => ({
  width: isMobile ? '100%' : '417px',
  minWidth: isMobile ? '290px' : 'auto',
  background: '#FFFFFF',
  boxShadow: '0px 10px 26px rgba(0, 0, 0, 0.04)',
  borderRadius: isMobile ? '12px' : '16px',
  overflow: 'visible',
  fontFamily: "'Red Hat Display', sans-serif",
  maxWidth: '100%',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.08)',
  },
}));

const ImageSection = styled(Box)(({ isMobile, customMobileHeight }) => ({
  width: '100%',
  height: isMobile ? (customMobileHeight || '156px') : '217px',
  position: 'relative',
  borderRadius: isMobile ? '12px' : '20px',
  overflow: 'hidden',
  marginBottom: isMobile ? '14px' : '18px',
}));

const CampaignImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 'inherit',
});

const TaxBenefitChip = styled(Box)(({ isMobile }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  background: 'linear-gradient(295deg, #EC1B26, #FF4A60)',
  color: '#FFFFFF',
  fontFamily: 'Red Hat Display, sans-serif',
  fontWeight: 700,
  fontSize: isMobile ? '10px' : '12px',
  lineHeight: '16px',
  letterSpacing: '-0.24px',
  height: isMobile ? '28px' : '36px',
  padding: isMobile ? '6px 10px' : '6px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTopLeftRadius: '3px',
  borderTopRightRadius: '20px',
  borderBottomLeftRadius: '20px',
  borderBottomRightRadius: '3px',
  whiteSpace: 'nowrap',
}));

const ProgressContainer = styled(Box)(({ isMobile }) => ({
  position: 'relative',
  marginBottom: isMobile ? '40px' : '50px',
}));

const ProgressLabel = styled(Box)(({ isMobile }) => ({
  position: 'absolute',
  top: '0px',
  background: '#EC1B26',
  color: '#FFFFFF',
  fontFamily: 'Red Hat Display, sans-serif',
  fontWeight: 700,
  fontSize: isMobile ? '12px' : '13px',
  lineHeight: '1',
  height: isMobile ? '20px' : '22px',
  borderRadius: '4px',
  transform: 'translateX(50%)',
  padding: isMobile ? '0 6px' : '0 8px',
  display: 'flex',
  alignItems: 'center',
}));

const StatsContainer = styled(Box)(({ isMobile }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: isMobile ? '16px' : '20px',
  gap: isMobile ? '6px' : '8px',
  marginTop: isMobile ? '8px' : '10px',
}));

const StatItem = styled(Box)(({ isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : '10px',
  height: isMobile ? '32px' : '38px',
  flex: 1,
  minWidth: 0,
}));

const IconContainer = styled(Box)(({ isMobile }) => ({
  width: isMobile ? '28px' : '32px',
  height: isMobile ? '28px' : '30px',
  background: 'rgba(236, 27, 38, 0.1)',
  border: '0.25px solid rgba(242, 178, 186, 0.1)',
  borderRadius: '7px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& img': {
    width: isMobile ? '14px' : '18px',
    height: isMobile ? '14px' : '18px',
    objectFit: 'contain',
  },
}));

const StatValue = styled(Typography)(({ isMobile }) => ({
  fontFamily: 'Red Hat Display',
  fontWeight: 700,
  fontSize: isMobile ? '12px' : '14px',
  lineHeight: isMobile ? '16px' : '18px',
  color: '#1D1D1B',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}));

const StatLabel = styled(Typography)(({ isMobile }) => ({
  fontFamily: 'Red Hat Display',
  fontWeight: 500,
  fontSize: isMobile ? '12px' : '14px',
  lineHeight: isMobile ? '16px' : '18px',
  color: '#686866',
  textTransform: 'capitalize',
}));

const SocialIconButton = styled(IconButton)(({ isMobile }) => ({
  width: isMobile ? '28px' : '40px',
  height: isMobile ? '28px' : '40px',
  border: '1px solid #E8E8E8',
  borderRadius: '10px',
  padding: '0px',
  transition: 'all 0.2s ease',
  '&:hover': { backgroundColor: '#f8f8f8', borderColor: '#d0d0d0' },
  '& img': {
    width: isMobile ? '16px' : '24px',
    height: isMobile ? '16px' : '24px',
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (amount) => formatCompactInr(amount);
const calculateProgress = (raised, goal) => Math.min(Math.round((raised / goal) * 100), 100);

// ─── Component ────────────────────────────────────────────────────────────────

const LifeCardV2 = ({ lifeData, customMobileImageHeight }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [shareLoading, setShareLoading] = useState({});

  const {
    id,
    image,
    taxBenefit = 'Tax Benefit',
    title,
    goalAmount = 0,
    raisedAmount = 0,
    backers = 0,
    hide_goal = 0,
    hide_raised = 0,
    socialMediaLinks,
  } = lifeData || {};

  const hideGoal = Number(hide_goal) || 0;
  const hideRaised = Number(hide_raised) || 0;
  const progressPercentage = calculateProgress(raisedAmount, goalAmount);

  // Resolve /life/:slug URL from redirects
  const slug =
    lifeData?.redirects &&
    Array.isArray(lifeData.redirects) &&
    lifeData.redirects[0]?.primary_url
      ? lifeData.redirects[0].primary_url
      : id;
  const detailPath = slug ? `/life/${slug}` : '/life';

  // Social icons
  const availableSocialPlatforms = getAvailableSocialPlatforms(socialMediaLinks);

  const handleCardClick = () => navigate(detailPath);
  const handleDonateClick = (e) => { e.stopPropagation(); navigate(detailPath); };

  const handleSocialShare = async (platform, e) => {
    e.stopPropagation();
    setShareLoading((prev) => ({ ...prev, [platform]: true }));
    try {
      const shareData = await getCampaignShareDetails(id);
      if (shareData) {
        const keyMap = { Facebook: 'facebook', LinkedIn: 'linkedin', Twitter: 'twitter' };
        const platformKey = keyMap[platform];
        const campaignUrl = shareData.share_links?.copy || shareData.link || '';
        const url =
          shareData.share_links?.[platformKey] ||
          buildSocialShareUrl(platformKey, campaignUrl, shareData.message);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error(`Failed to share on ${platform}:`, err);
    } finally {
      setShareLoading((prev) => ({ ...prev, [platform]: false }));
    }
  };

  const imageSrc = typeof image === 'string' ? getImageSrc(image) : image;

  return (
    <Box>
      <StyledCard isMobile={isMobile} onClick={handleCardClick}>
        <CardContent sx={{ padding: isMobile ? '12px' : '16px', '&:last-child': { paddingBottom: isMobile ? '16px' : '20px' } }}>

          {/* ── Image ── */}
          <ImageSection isMobile={isMobile} customMobileHeight={customMobileImageHeight}>
            <CampaignImage src={imageSrc || Images.fallBackImageCard} alt={title || 'Life campaign'} />
            <TaxBenefitChip isMobile={isMobile}>{taxBenefit}</TaxBenefitChip>
          </ImageSection>

          {/* ── Main content ── */}
          <Box sx={{ width: '100%' }}>

            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Red Hat Display',
                fontWeight: 700,
                fontSize: isMobile ? '14px' : '18px',
                lineHeight: isMobile ? '18px' : '26px',
                color: '#1D1D1B',
                textTransform: 'capitalize',
                marginBottom: isMobile ? '8px' : '10px',
                minHeight: isMobile ? '36px' : '52px',
                maxHeight: isMobile ? '36px' : '52px',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {title}
            </Typography>

            {/* Progress bar */}
            <ProgressContainer isMobile={isMobile}>
              <Box sx={{ position: 'absolute', width: '100%', height: isMobile ? '10px' : '12px', left: '0px', top: isMobile ? '3px' : '4px', background: '#E8E8E8', opacity: 0.6, borderRadius: '20px' }} />
              <Box sx={{ position: 'absolute', width: `${progressPercentage}%`, height: '6px', left: '3px', top: isMobile ? '6px' : '7px', background: 'linear-gradient(90deg, #FF4A60 0%, #EC1B26 100%)', borderRadius: '4px' }} />
              <ProgressLabel
                isMobile={isMobile}
                sx={{
                  left:
                    progressPercentage === 0
                      ? isMobile ? '0px' : '-20px'
                      : progressPercentage <= 10
                        ? isMobile ? '-8px' : '-10px'
                        : progressPercentage >= 90
                          ? isMobile ? 'calc(100% - 60px)' : 'calc(100% - 72px)'
                          : `calc(${progressPercentage}% - ${isMobile ? '18px' : '22px'})`,
                }}
              >
                {`${progressPercentage}%`}
              </ProgressLabel>
            </ProgressContainer>

            {/* Stats */}
            <StatsContainer isMobile={isMobile}>

              {/* Case 1: show Goal + Raised + Donors */}
              {hideGoal === 0 && hideRaised === 0 && (
                <>
                  <StatItem isMobile={isMobile}>
                    <IconContainer isMobile={isMobile}>
                      <img src={Images.cardGoal} alt="Goal" />
                    </IconContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <StatLabel isMobile={isMobile}>Goal</StatLabel>
                      <StatValue isMobile={isMobile}>{formatCurrency(goalAmount)}</StatValue>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                  <StatItem isMobile={isMobile}>
                    <IconContainer isMobile={isMobile}>
                      <img src={Images.cardRaised} alt="Raised" />
                    </IconContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <StatLabel isMobile={isMobile} sx={{ fontWeight: 700 }}>Raised</StatLabel>
                      <StatValue isMobile={isMobile} sx={{ fontWeight: 800 }}>{formatCurrency(raisedAmount)}</StatValue>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                </>
              )}

              {/* Case 2: hide goal only — show Raised + descriptive text + Donors */}
              {hideGoal === 1 && hideRaised === 0 && (
                <>
                  <StatItem isMobile={isMobile}>
                    <IconContainer isMobile={isMobile}>
                      <img src={Images.cardRaised} alt="Raised" />
                    </IconContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <StatLabel isMobile={isMobile} sx={{ fontWeight: 700 }}>Raised</StatLabel>
                      <StatValue isMobile={isMobile} sx={{ fontWeight: 800 }}>{formatCurrency(raisedAmount)}</StatValue>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                  <StatItem isMobile={isMobile} sx={{ justifyContent: 'center', flex: 1 }}>
                    <StatLabel isMobile={isMobile} sx={{ fontSize: isMobile ? '10px' : '12px', lineHeight: isMobile ? '14px' : '16px', whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' }}>
                      For the Profiled patient and other patient in need
                    </StatLabel>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                </>
              )}

              {/* Case 3: hide both — descriptive text + Donors */}
              {hideGoal === 1 && hideRaised === 1 && (
                <>
                  <StatItem isMobile={isMobile} sx={{ flex: 1 }}>
                    <StatLabel isMobile={isMobile} sx={{ fontSize: isMobile ? '10px' : '12px', lineHeight: isMobile ? '14px' : '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      For the Profiled patient and other patient in need
                    </StatLabel>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                </>
              )}

              {/* Donors — always shown */}
              <StatItem isMobile={isMobile}>
                <IconContainer isMobile={isMobile}>
                  <img src={Images.cardBackers} alt="Donors" />
                </IconContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <StatLabel isMobile={isMobile}>Donors</StatLabel>
                  <StatValue isMobile={isMobile}>{Number(backers).toLocaleString()}</StatValue>
                </Box>
              </StatItem>
            </StatsContainer>

            <Divider sx={{ marginBottom: isMobile ? '16px' : '20px' }} />

            {/* Footer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', height: isMobile ? '32px' : '44px' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}
                onClick={(e) => e.stopPropagation()}
              >
                {availableSocialPlatforms.facebook && (
                  <SocialIconButton isMobile={isMobile} onClick={(e) => handleSocialShare('Facebook', e)} aria-label="Share on Facebook" disabled={shareLoading.Facebook}>
                    <img src={Images.facebook} alt="Facebook" />
                  </SocialIconButton>
                )}
                {availableSocialPlatforms.linkedin && (
                  <SocialIconButton isMobile={isMobile} onClick={(e) => handleSocialShare('LinkedIn', e)} aria-label="Share on LinkedIn" disabled={shareLoading.LinkedIn}>
                    <img src={Images.linkedin} alt="LinkedIn" />
                  </SocialIconButton>
                )}
                {availableSocialPlatforms.twitter && (
                  <SocialIconButton isMobile={isMobile} onClick={(e) => handleSocialShare('Twitter', e)} aria-label="Share on Twitter" disabled={shareLoading.Twitter}>
                    <img src={Images.twitter} alt="Twitter" />
                  </SocialIconButton>
                )}
              </Box>
              <DonateButtonVariant text="Donate Now" onClick={handleDonateClick} isMobile={isMobile} />
            </Box>

          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default LifeCardV2;
